import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { Metodosui } from '../../services/metodosui';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';
import { Modelogeneraldatos } from '../../models/modelogeneraldatos';

export interface ActividadPendiente {
  id: number;
  trabajoId: number;
  fecha: string;
  lugar: string;
  descripcion: string;
  monto: number;
  extra: number;
  descuento: number;
  subtotal: number;
  turno: string;
  comentario: string;
  seleccionado: boolean;
  subcontrato: boolean;
  subcontratoMontoHist?: number;
  subcontratoExtraHist?: number;
  subcontratoDescuentoHist?: number;
}

export interface GrupoPendiente {
  idGrupo: string;
  fecha: string;
  turno: string;
  items: ActividadPendiente[];
  seleccionado: boolean;
  puedeUnificar: boolean;
  unificado: boolean;
  montoUnificado: number;
  esSubcontrato?: boolean;
  subcontratoMonto?: number;
  subcontratoExtra?: number;
  subcontratoDescuento?: number;
  subcontratoSaldo?: number;
  subcontratoMontoConExtra?: number;
  descripcionSubcontrato?: string;
}


@Component({
  selector: 'app-generar-pago',
  templateUrl: './generar-pago.component.html',
  styleUrls: ['./generar-pago.component.scss'],
  standalone: false,
})
export class GenerarPagoComponent implements OnInit {
  @Input() trabajadorId!: number;
  @Input() nombreTrabajador!: string;
  @Input() isEdit: boolean = false;
  @Input() pagoId?: number;

  pendientes: ActividadPendiente[] = [];
  grupos: GrupoPendiente[] = [];
  savedActividadIds: number[] = [];
  mostrarPendientes: boolean = false;

  get gruposVisibles(): GrupoPendiente[] {
    if (!this.isEdit || this.mostrarPendientes) {
      return this.grupos;
    }
    // Si estamos editando y NO queremos ver pendientes, solo mostramos los grupos
    // que tienen al menos un item que fue guardado previamente en este pago.
    return this.grupos.filter(g => g.items.some(i => this.savedActividadIds.includes(i.id)));
  }

  comentario: string = '';
  fechaPago: string = new Date().toISOString().split('T')[0];

  montoTotal = 0;
  extrasTotal = 0;
  descuentosTotal = 0;
  totalPagar = 0;

  observationTemplates: any[] = [];
  originalComentario: string = '';

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosUi: Metodosui,
    private metodosGenService: MetodosgenService
  ) {}

  async ngOnInit() {
    this.loadObservationTemplates();
    if (this.isEdit && this.pagoId) {
      await this.cargarPagoEdit();
    } else {
      await this.cargarPendientes();
    }
  }

  cerrar() {
    this.modalCtrl.dismiss();
  }

  async cargarPagoEdit() {
    await this.metodosUi.showLoading('Cargando pago...');
    try {
      const userStr = localStorage.getItem('user');
      const usuario = userStr ? JSON.parse(userStr).l_usuario : '';

      const payload = {
        datos: {
          dt1: 'GET_PAGO_POR_ID',
          dt2: String(this.pagoId)
        },
        usuario: usuario
      };

      const res: any = await firstValueFrom(this.javapiService.genericPost('api/pg/trabajos/planilla/pagos/crud', payload));
      
      if (res && res.length > 0) {
        const data = res[0];
        this.comentario = data.dt7 || '';
        this.originalComentario = this.comentario;
        this.fechaPago = data.dt10 || new Date().toISOString().split('T')[0];
        
        let savedJson: any = null;
        try {
          savedJson = JSON.parse(data.dt8);
          if (savedJson && savedJson.actividad_ids) {
            this.savedActividadIds = savedJson.actividad_ids;
          }
        } catch (e) {}

        // Cargar las actividades y agruparlas
        await this.cargarPendientes(false); // false para que no muestre loading de nuevo

        // Aplicar la configuración del JSON guardado a los grupos actuales
        if (savedJson && savedJson.grupos) {
          for (const savedG of savedJson.grupos) {
            const currentG = this.grupos.find(g => g.idGrupo === savedG.idGrupo);
            if (currentG) {
              currentG.unificado = savedG.unificado;
              if (savedG.unificado) {
                currentG.montoUnificado = savedG.montoUnificado;
              }
            }
          }
          this.calcularTotales();
        }
      }
    } catch (error) {
      console.error(error);
      this.metodosUi.showToast('Error al cargar pago para edición', 'danger');
    } finally {
      this.metodosUi.hideLoading();
    }
  }

  async cargarPendientes(showLoader: boolean = true) {
    if (showLoader) await this.metodosUi.showLoading('Cargando registros...');
    try {
      const userStr = localStorage.getItem('user');
      const usuario = userStr ? JSON.parse(userStr).l_usuario : '';

      const payload = {
        datos: {
          dt1: 'GET_PENDIENTES',
          dt2: this.pagoId ? String(this.pagoId) : '',
          dt3: String(this.trabajadorId)
        },
        usuario: usuario
      };

      const res: any = await firstValueFrom(this.javapiService.genericPost('api/pg/trabajos/planilla/pagos/crud', payload));
      
      this.pendientes = (res || []).map((item: any) => {
        const monto = Number(item.dt4) || 0;
        const extra = Number(item.dt5) || 0;
        const descuento = Number(item.dt6) || 0;

        const subInfo = (item.dt13 || '').split('_');
        const isSub = subInfo[0] === 'S';
        const subMontoHist = isSub ? Number(subInfo[1]) || 0 : 0;
        const subExtraHist = isSub ? Number(subInfo[2]) || 0 : 0;
        const subDescHist = isSub ? Number(subInfo[3]) || 0 : 0;

        return {
          id: Number(item.dt1),
          trabajoId: Number(item.dt10) || 0,
          fecha: item.dt2,
          lugar: item.dt3,
          monto: monto,
          extra: extra,
          descuento: descuento,
          subtotal: monto + extra - descuento, // Recalculamos para mayor precisión
          turno: item.dt8,
          descripcion: item.dt9,
          comentario: item.dt11,
          seleccionado: this.isEdit ? this.savedActividadIds.includes(Number(item.dt1)) : true,
          subcontrato: isSub,
          subcontratoMontoHist: subMontoHist,
          subcontratoExtraHist: subExtraHist,
          subcontratoDescuentoHist: subDescHist
        };
      });

      // Agrupar por Fecha + Turno (normales) o por Trabajo (subcontratos)
      const gruposMap = new Map<string, GrupoPendiente>();
      
      for (const p of this.pendientes) {
        const idGrupo = p.subcontrato ? 'subcontrato_' + p.trabajoId : p.fecha + '_' + p.turno;
        if (!gruposMap.has(idGrupo)) {
          gruposMap.set(idGrupo, {
            idGrupo,
            fecha: p.fecha,
            turno: p.turno,
            items: [],
            seleccionado: false, // se recalcula después
            puedeUnificar: false,
            unificado: false,
            montoUnificado: 0,
            esSubcontrato: p.subcontrato
          });
        }
        gruposMap.get(idGrupo)!.items.push(p);
      }

      this.grupos = Array.from(gruposMap.values()).map(g => {
        g.puedeUnificar = !g.esSubcontrato && g.items.length > 1;
        // Si se puede unificar, por defecto sugerimos el monto mayor de los trabajos (ej: si hay 35 y 35, sugiere 35)
        if (g.puedeUnificar) {
          g.montoUnificado = Math.max(...g.items.map(i => i.monto));
        } else {
          g.montoUnificado = g.items.length > 0 ? g.items[0].monto : 0;
        }
        g.seleccionado = g.items.some(i => i.seleccionado);

        if (g.esSubcontrato) {
          const firstItem = g.items[0];
          g.subcontratoMonto = firstItem?.subcontratoMontoHist || 0;
          g.subcontratoExtra = firstItem?.subcontratoExtraHist || 0;
          g.subcontratoDescuento = firstItem?.subcontratoDescuentoHist || 0;
          g.subcontratoSaldo = g.subcontratoMonto + g.subcontratoExtra - g.subcontratoDescuento;
          g.subcontratoMontoConExtra = g.subcontratoMonto + g.subcontratoExtra;
          g.descripcionSubcontrato = g.items.find(i => i.descripcion)?.descripcion || g.items[0]?.descripcion || '';
        }

        return g;
      });


      this.calcularTotales();

    } catch (error) {
      console.error(error);
      this.metodosUi.showToast('Error al cargar datos pendientes', 'danger');
    } finally {
      if (showLoader) this.metodosUi.hideLoading();
    }
  }

  toggleSeleccion(act: ActividadPendiente, grupo: GrupoPendiente, event?: any) {
    if (event && event.detail !== undefined) {
      act.seleccionado = event.detail.checked;
    } else {
      act.seleccionado = !act.seleccionado;
    }
    
    // El check del grupo debe estar marcado si al menos uno de sus items está seleccionado
    grupo.seleccionado = grupo.items.some(i => i.seleccionado);
    this.calcularTotales();
  }

  toggleGrupo(grupo: GrupoPendiente, event?: any) {
    let isChecked: boolean;
    
    if (event && event.detail !== undefined) {
      // Viene directamente del cambio en el ion-checkbox
      isChecked = event.detail.checked;
    } else {
      // Viene de un clic en el área de la cabecera
      isChecked = !grupo.seleccionado;
      grupo.seleccionado = isChecked; // Actualizamos el modelo manualmente
    }

    // Aplicar el estado a todos los hijos
    grupo.items.forEach(p => p.seleccionado = isChecked);
    this.calcularTotales();
  }

  toggleTodos(event: any) {
    const isChecked = event.detail.checked;
    this.pendientes.forEach(p => p.seleccionado = isChecked);
    this.grupos.forEach(g => g.seleccionado = isChecked);
    this.calcularTotales();
  }

  calcularTotales() {
    this.montoTotal = 0;
    this.extrasTotal = 0;
    this.descuentosTotal = 0;
    this.totalPagar = 0;

    for (const g of this.grupos) {
      // Solo tomamos en cuenta los grupos que tienen algún item seleccionado
      const itemsSeleccionados = g.items.filter(i => i.seleccionado);
      if (itemsSeleccionados.length === 0) continue;

      let extraG = 0;
      let descG = 0;
      let montoBaseG = 0;

      for (const p of itemsSeleccionados) {
        extraG += p.extra;
        descG += p.descuento;
        montoBaseG += p.monto;
      }

      // Si el grupo está unificado, reemplazamos la suma de montos por el monto unificado
      if (g.unificado && g.puedeUnificar) {
        montoBaseG = g.montoUnificado;
      }

      this.montoTotal += montoBaseG;
      this.extrasTotal += extraG;
      this.descuentosTotal += descG;
      this.totalPagar += (montoBaseG + extraG - descG);
    }
  }

  cambioMontoUnificado() {
    this.calcularTotales();
  }

  async generarPago() {
    const seleccionados = this.pendientes.filter(p => p.seleccionado);
    if (seleccionados.length === 0) {
      this.metodosUi.showToast('Debe seleccionar al menos un registro', 'warning');
      return;
    }

    const numSubcontratos = this.grupos.filter(g => g.esSubcontrato && g.seleccionado).length;
    let mensajeConfirm = `Se ${this.isEdit ? 'actualizará' : 'generará'} un pago por $${this.totalPagar.toFixed(2)} para ${this.nombreTrabajador}.`;
    if (numSubcontratos > 0) {
      mensajeConfirm += ` Se confirmará(n) ${numSubcontratos} subcontrato(s) como pagado(s).`;
    }

    const confirm = await this.metodosUi.showConfirmation(
      this.isEdit ? '¿Actualizar Pago?' : '¿Confirmar Pago?',
      mensajeConfirm,
      'Confirmar'
    );

    if (!confirm) return;

    await this.metodosUi.showLoading('Procesando pago...');
    try {
      const userStr = localStorage.getItem('user');
      const usuario = userStr ? JSON.parse(userStr).l_usuario : '';
      
      const idsStr = seleccionados.map(s => s.id).join(',');
      
      // Construimos un JSON minimalista para evitar redundancia de datos
      const detallesGrupo = this.grupos.filter(g => g.items.some(i => i.seleccionado)).map(g => {
        const itemsSeleccionados = g.items.filter(i => i.seleccionado);
        return {
          idGrupo: g.idGrupo,
          unificado: g.unificado,
          montoUnificado: g.unificado ? g.montoUnificado : null,
          trabajo_ids: [...new Set(itemsSeleccionados.map(i => i.trabajoId))],
          items: itemsSeleccionados.map(i => ({
            id: i.id,
            monto: i.monto,
            extra: i.extra,
            descuento: i.descuento
          }))
        };
      });

      const detalleJson = JSON.stringify({
        actividad_ids: seleccionados.map(s => s.id),
        trabajo_ids: [...new Set(seleccionados.map(s => s.trabajoId))],
        grupos: detallesGrupo
      });


      const payload = {
        datos: {
          dt1: this.isEdit ? 'UPDATE_PAGO' : 'SAVE_PAGO',
          dt2: this.isEdit ? String(this.pagoId) : '',
          dt3: String(this.trabajadorId),
          dt4: this.fechaPago.split('T')[0], // Solo la fecha
          dt5: String(this.montoTotal),
          dt6: String(this.extrasTotal),
          dt7: String(this.descuentosTotal),
          dt8: String(this.totalPagar),
          dt9: this.comentario,
          dt10: detalleJson,
          dt11: idsStr
        },
        usuario: usuario
      };

      const res: any = await firstValueFrom(this.javapiService.genericPost('api/pg/trabajos/planilla/pagos/crud', payload));
      
      this.metodosUi.showToast(this.isEdit ? 'Pago actualizado exitosamente' : 'Pago registrado exitosamente', 'success');
      this.modalCtrl.dismiss({ action: 'saved' });

    } catch (error) {
      console.error(error);
      this.metodosUi.showToast('Error al registrar pago', 'danger');
    } finally {
      this.metodosUi.hideLoading();
    }
  }

  async anularPago() {
    const confirm = await this.metodosUi.showConfirmation(
      '¿Anular Pago?',
      `Esta acción liberará los registros y eliminará el pago de ${this.nombreTrabajador} del sistema.`,
      'Anular'
    );

    if (!confirm) return;

    await this.metodosUi.showLoading('Anulando pago...');
    try {
      const userStr = localStorage.getItem('user');
      const usuario = userStr ? JSON.parse(userStr).l_usuario : '';

      const payload = {
        datos: {
          dt1: 'DELETE_PAGO',
          dt2: String(this.pagoId)
        },
        usuario: usuario
      };

      await firstValueFrom(this.javapiService.genericPost('api/pg/trabajos/planilla/pagos/crud', payload));
      
      this.metodosUi.showToast('Pago anulado exitosamente', 'success');
      this.modalCtrl.dismiss({ action: 'saved' }); // 'saved' fuerza la recarga del historial
    } catch (error) {
      console.error(error);
      this.metodosUi.showToast('Error al anular pago', 'danger');
    } finally {
      this.metodosUi.hideLoading();
    }
  }

  async loadObservationTemplates() {
    try {
      const res: any = await firstValueFrom(this.javapiService.getJsonParam('CT_PLANILLA_PAGOS'));
      if (res && res.pa_json) {
        this.observationTemplates = JSON.parse(res.pa_json);
      }
    } catch (e) {
      console.error('Error loading observation templates', e);
    }
  }

  applyTemplate(template: any) {
    if (template === 'clear') {
      this.comentario = this.originalComentario;
      return;
    }

    let textoProcesado = template.texto;

    // Procesar variables dinámicas
    if (textoProcesado.includes('$semana_ini') || textoProcesado.includes('$semana_fin')) {
      const fechaBase = new Date(this.fechaPago + 'T12:00:00'); // T12:00:00 para evitar problemas de zona horaria
      
      // Calcular Lunes ($semana_ini)
      const lunes = new Date(fechaBase);
      const diaSemana = lunes.getDay(); // 0 (Dom) a 6 (Sab)
      const diff = lunes.getDate() - diaSemana + (diaSemana === 0 ? -6 : 1); // Ajustar a Lunes
      lunes.setDate(diff);

      textoProcesado = textoProcesado.replace(/\$semana_ini/g, this.formatDateToSpanish(lunes));
      textoProcesado = textoProcesado.replace(/\$semana_fin/g, this.formatDateToSpanish(fechaBase));
    }

    const current = this.comentario || '';
    if (current.includes(textoProcesado)) {
      this.comentario = current.replace(textoProcesado, '').replace('\n\n', '\n').trim();
    } else {
      this.comentario = (current + (current ? '\n\n' : '') + textoProcesado).trim();
    }
  }

  private formatDateToSpanish(date: Date): string {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
}
